import { ProgressFragment } from "@devforce/trailhead-client-gql";
import {
  CollectionItem,
  CollectionQuery,
  CollectionState,
  getUnifiedCollectionItem,
} from "common/contentCollection";
import { QueryVariables, verifiedIncompleteResult } from "../util/filterHelper";
import { gql, QueryAdapterVariables } from "common/graphqlAdapter";

import type {
  QueryAdapterOptions,
  QueryAdapterValue,
} from "common/graphqlAdapter";
import type {
  SearchResultType,
  SearchResultConnectionType,
} from "@devforce/trailhead-client-gql";

type UnifiedDataResult = {
  [queryName: string]: SearchResultConnectionType;
};

type QueryResult = QueryAdapterValue<UnifiedDataResult>;

// Can be broken down into a query and a fragment and live in https://github.com/devforce/trailhead-client/tree/main/packages/gql/src/
const Query = gql`
  query LxUnifiedIndexPage(
    $query: String!
    $first: Int = 10
    $before: String
    $after: String
    $sort: SearchSort
    $filter: SearchFilterInput
    $searchAnalyticsPayload: String
    $tab: SearchTab
  ) {
    indexPageSearch(
      criteria: { text: $query, sort: $sort }
      first: $first
      before: $before
      after: $after
      filter: $filter
      searchAnalyticsPayload: $searchAnalyticsPayload
      tab: $tab
    ) {
      __typename
      searchAnalyticsPayload
      totalCount
      nodes {
        __typename
        clickAnalyticsPayload
        unified {
          __typename
          id
          type
          label
          description
          body
          name
          url
          iconUrl
          color
          pointTotal
          minuteTotal
          secondTotal
          likesCount
          commentsCount
          creatorName
          discussingCount
          membersCount
          favorite
          actionAt
          keywords
          progress {
            ...Progress
          }
          userName
          userImageUrl
          userProfileUrl
          userRole
          userTitle
          userCompany
          userLocation
          rating
          releaseName
          releaseVersion
          status
          postedIn
          postedInUrl
          updatedAt
        }
      }
      pageInfo {
        __typename
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
    }
  }
  ${ProgressFragment}
`;

export class UnifiedCollectionQuery implements CollectionQuery<QueryResult> {
  public searchAnalyticsPayload: string | null = null;
  private clickAnalyticsPayload = new Map<string, string>();
  private config: QueryAdapterVariables;

  constructor(config: QueryAdapterVariables) {
    this.config = config;
  }

  getCollectionItems(result: QueryResult): CollectionItem[] {
    const collection = this.getCollection(result);

    if (collection == null || collection.nodes == null) return [];

    this.searchAnalyticsPayload = collection.searchAnalyticsPayload ?? null;

    return collection.nodes
      .map((node) => this.collectClickAnalyticsPayload(node))
      .map((node) =>
        node?.unified ? getUnifiedCollectionItem(node.unified) : null
      )
      .filter((node) => node != null)
      .filter((node) => {
        const variables: QueryVariables = this.config.variables;
        const progressFilter = variables?.filter?.progress;
        return verifiedIncompleteResult(node!, progressFilter);
      }) as CollectionItem[];
  }

  getQueryOptions(state?: CollectionState): QueryAdapterOptions {
    return {
      query: Query,
      variables: {
        after: state?.endCursor ?? undefined,
        ...this.config.variables,
      },
    };
  }

  getTotalItemCount(result: QueryResult): number | undefined {
    const collection = this.getCollection(result);

    return collection?.totalCount ?? undefined;
  }

  getState(result: QueryResult) {
    return {
      endCursor: this.getCollection(result)?.pageInfo.endCursor,
    };
  }

  hasMore(result: QueryResult) {
    return this.getCollection(result)?.pageInfo.hasNextPage;
  }

  getClickAnalyticsEventData(id: string) {
    return this.clickAnalyticsPayload.get(id);
  }

  private getCollection(result: QueryResult) {
    return (result.data && result.data.indexPageSearch) || undefined;
  }

  private collectClickAnalyticsPayload(node: SearchResultType | null) {
    if (!node || !node.unified?.id || !node.clickAnalyticsPayload) return node;

    this.clickAnalyticsPayload.set(node.unified.id, node.clickAnalyticsPayload);
    return node;
  }
}
